import * as angular from "angular";
import {ILogService} from "angular";
import {AgentInventoryConfigModel, AgentInventorySetting} from "../models/inventory-setting.model";
import * as _ from "lodash";

export class AgentInventoryDialogService {

  /* @ngInject */
  constructor(
    private $mdDialog,
    private $log: ILogService
  ) {
  }

  openAgentInventorySetting(event?, inventorySetting?: AgentInventorySetting, excludeIds?: string[]) {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: require('./../views/dialogs/edit-inventory-setting-dialog.html').default,
      targetEvent: event,
      clickOutsideToClose: true,
      fullscreen: true,
      controller: function ($scope, splitService) {

        $scope.header = inventorySetting ? 'Edit Agent Inventory Configuration' : 'Create Agent Inventory Configuration';
        $scope.inventorySetting = inventorySetting ? angular.copy(inventorySetting) : {};

        // Get package types from site context (bringoz-package-select uses this automatically)
        const sitePackageItems = splitService.getSiteInfo().itemSettings;

        const packageTypeOptions = sitePackageItems.map(item => {
          return item.id
        });
        $scope.packageTypeOptions = packageTypeOptions.filter(id => {
          return !_.includes(excludeIds, id);
        });

        // Initialize if new
        if (!$scope.inventorySetting.skills) {
          $scope.inventorySetting.skills = [];
        }

        $scope.cancel = self.$mdDialog.cancel;

        // ******************************
        // External methods
        // ******************************

        $scope.onPackageTypeChange = (packageType) => {
          $scope.inventorySetting.packageType = packageType;
        }

        $scope.onExternalIdChange = (externalId) => {
          $scope.inventorySetting.externalId = externalId;
        }

        $scope.onSkillsChange = (ids) => {
          $scope.inventorySetting.skills = ids;
        }

        $scope.onQuantityChange = (quantity) => {
          $scope.inventorySetting.quantity = quantity;
        }

        $scope.submit = (form) => {
          if (form) {
            form.$setSubmitted(true);
            if (form.$valid) {
              self.$mdDialog.hide($scope.inventorySetting);
            }
          } else {
            self.$log.error('Form resolved to undefined!. \n' +
              '--> agentInventoryDialogService@editInventorySetting@submit')
          }
        }

      }
    })
  }

  createTransaction(event, locationType?: string, locationId?: string, locationData?: any, agentId?: string) {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: require('./../views/dialogs/create-transaction-dialog.html').default,
      targetEvent: event,
      clickOutsideToClose: true,
      fullscreen: true,
      controller: function ($scope, splitService) {

        $scope.header = 'Create New Transaction';

        // Hide location type field if context is provided
        $scope.hideLocationType = !!locationType;
        $scope.isAgentSelectionEnabled = !agentId;

        $scope.transaction = {
          actionType: 'Load',
          locationType: locationType || 'DEPOT',
          locationId: locationId || null,
          agentId: agentId,
          packageType: null,
          quantity: 1,
          serviceId: null
        };

        // Action types
        $scope.actionTypes = [
          {id: 'Load', name: 'Load'},
          {id: 'Unload', name: 'Unload'}
        ];

        // Location types
        $scope.locationTypes = [
          {id: 'ACCOUNT', name: 'Account'},
          {id: 'DEPOT', name: 'Depot'}
        ];

        // Get package types from site context
        const sitePackageItems = splitService.getSiteInfo().itemSettings;
        const allPackageTypeIds = sitePackageItems.map(item => item.id);
        $scope.packageTypeOptions = allPackageTypeIds;

        // Store the full location object (account or depot) from selectors
        // Initialize with passed locationData if available
        let storedLocationData = locationData || null;

        // Store the full agent object from selector
        let storedAgentData = null;

        // Track max quantity for selected package
        let maxQuantity = null;

        $scope.cancel = self.$mdDialog.cancel;

        // ******************************
        // Helper methods
        // ******************************

        const updatePackageTypeOptions = () => {
          if ($scope.transaction.actionType === 'Load' && storedLocationData) {
            // Step 1: Filter by location config
            let filteredPackageTypes = allPackageTypeIds;

            if (storedLocationData.agentInventoryConfig?.settings?.length > 0) {
              // Step 2: If agent selected, filter by skills
              if (storedAgentData?.skills?.length > 0) {
                // Only show packages where agent has matching skills
                const agentSkills = storedAgentData.skills;
                const matchingSettings = storedLocationData.agentInventoryConfig.settings.filter(setting => {
                  // Check if agent has ANY skill that matches this setting's skills
                  return setting.skills?.every(skillRequired => agentSkills.includes(skillRequired));
                });
                filteredPackageTypes = matchingSettings.map(s => s.packageType);
              } else {
                // No agent or no skills, show all configured packages
                filteredPackageTypes = storedLocationData.agentInventoryConfig.settings.map(s => s.packageType);
              }
              $scope.packageTypeOptions = filteredPackageTypes;
            } else {
              $scope.packageTypeOptions = allPackageTypeIds;
            }
          } else if ($scope.transaction.actionType === 'Unload') {
            // NEW UNLOAD LOGIC - Filter by agent inventory
            if (storedAgentData) {
              // Agent selected - check for agentInventory
              if (storedAgentData.agentInventory?.items) {
                // Show only package types that exist in agent's inventory
                const inventoryPackageTypes = Object.keys(storedAgentData.agentInventory.items);
                $scope.packageTypeOptions = inventoryPackageTypes;
              } else {
                // Agent has no inventory - hide package types
                $scope.packageTypeOptions = [];
              }
            } else {
              // No agent selected yet - show empty list
              $scope.packageTypeOptions = [];
            }
          } else {
            $scope.packageTypeOptions = allPackageTypeIds;
          }
        }

        const updateMaxQuantity = () => {
          const packageType = $scope.transaction.packageType;

          if (!packageType) {
            maxQuantity = null;
            return;
          }

          if ($scope.transaction.actionType === 'Unload' && storedAgentData) {
            // For Unload: get max from agent's inventory items
            if (storedAgentData.agentInventory?.items?.[packageType]) {
              maxQuantity = storedAgentData.agentInventory.items[packageType];
            } else {
              maxQuantity = null;
            }
          } else if ($scope.transaction.actionType === 'Load' && storedLocationData) {
            // For Load: get max from location's agentInventoryConfig
            if (storedLocationData.agentInventoryConfig?.settings) {
              const setting = storedLocationData.agentInventoryConfig.settings.find(s => s.packageType === packageType);
              if (setting?.quantity) {
                maxQuantity = setting.quantity;
              } else {
                maxQuantity = null;
              }
            } else {
              maxQuantity = null;
            }
          } else {
            maxQuantity = null;
          }

          // If current quantity exceeds max, reset it
          if (maxQuantity !== null && $scope.transaction.quantity > maxQuantity) {
            $scope.transaction.quantity = maxQuantity;
          }
        };

        // Initial filter if location data was passed
        if (storedLocationData) {
          updatePackageTypeOptions();
        }

        // ******************************
        // External methods
        // ******************************

        $scope.onActionTypeChange = () => {
          // Clear package type and quantity when switching action type
          $scope.transaction.packageType = null;
          $scope.transaction.quantity = 1;
          // Update package options when action type changes
          updatePackageTypeOptions();
          updateMaxQuantity();
        };

        $scope.onAgentSelect = (agent, id) => {
          if (id) {
            storedAgentData = agent; // Store the full agent object!
            $scope.transaction.agentId = id;
            // Clear package type and quantity when switching agent
            $scope.transaction.packageType = null;
            $scope.transaction.quantity = 1;
            updatePackageTypeOptions(); // Re-filter packages based on agent skills
            updateMaxQuantity();
          } else {
            storedAgentData = null;
            $scope.transaction.agentId = null;
            $scope.transaction.packageType = null;
            $scope.transaction.quantity = 1;
            updatePackageTypeOptions(); // Reset to location-only filtering
            updateMaxQuantity();
          }
        };

        $scope.onAccountSelect = (account) => {
          if (account) {
            storedLocationData = account; // Store the full account object!
            $scope.transaction.locationId = account.id;
            updatePackageTypeOptions();
            updateMaxQuantity();
          } else {
            storedLocationData = null;
            $scope.transaction.locationId = null;
          }
        };

        $scope.onDepotSelect = (depot) => {
          if (depot) {
            storedLocationData = depot; // Store the full depot object!
            $scope.transaction.locationId = depot.id;
            updatePackageTypeOptions();
            updateMaxQuantity();
          } else {
            storedLocationData = null;
            $scope.transaction.locationId = null;
          }
        };

        $scope.onLocationIdChange = (locationId) => {
          // Clear storedLocationData for manual entry
          storedLocationData = null;
          $scope.transaction.locationId = locationId;
          updatePackageTypeOptions();
        };

        $scope.onPackageTypeChange = (packageType) => {
          $scope.transaction.packageType = packageType;
          updateMaxQuantity(); // Calculate max quantity for selected package
        };

        $scope.getMaxQuantity = () => {
          return maxQuantity;
        };

        $scope.submit = (form) => {
          if (form) {
            form.$setSubmitted(true);
            if (form.$valid) {
              // Build transaction object with only needed fields
              const transactionPayload: any = {
                actionType: $scope.transaction.actionType,
                locationType: $scope.transaction.locationType,
                locationId: $scope.transaction.locationId,
                agentId: $scope.transaction.agentId,
                packageType: $scope.transaction.packageType,
                quantity: $scope.transaction.quantity
              };

              // Add serviceId only if it has a value
              if ($scope.transaction.serviceId) {
                transactionPayload.serviceId = $scope.transaction.serviceId;
              }

              self.$mdDialog.hide(transactionPayload);
            }
          } else {
            self.$log.error('Form resolved to undefined!. \n' +
              '--> agentInventoryDialogService@createTransaction@submit')
          }
        };

      }
    })
  }

}

angular
  .module('bringoz.admin.agent-inventory')
  .service('agentInventoryDialogService', AgentInventoryDialogService);

